import { BillingResidency } from '@alpod-ai-writer/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button, Label, Spinner, TextInput } from 'flowbite-react';
import { AlertTriangleIcon, Check, CheckIcon, CreditCardIcon, LandmarkIcon, Percent, TagIcon } from 'lucide-react';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { ErrorMessage } from '../../../components/errorMessage/errorMessage.component';
import { clearPackageSelections } from '../../../lib/creditSelections.utils';
import { getReturnPath as getCreditTopUpReturnPath } from '../../../lib/creditTopUpReturn.utils';
import { getCurrencyFormatter } from '../../../lib/format.utils';
import { pathWithParams } from '../../../lib/routing.utils';
import { BillingForm } from '../../components/account/billingForm.component';
import { AppHeader } from '../../components/appHeader/appHeader.component';
import { DataPrivacyModal } from '../../modals/dataPrivacy/dataPrivacyModal.component';
import { TermsAndConditionsModal } from '../../modals/termsAndConditions/termsAndConditionsModal.component';
import { ToastNotificationType } from '../../components/toastNotification/toastNotifications.context';
import { useToastNotifications } from '../../components/toastNotification/useToastNotifications.hook';
import { useSession } from '../../hooks/useSession.hook';
import { RouteIndex } from '../../routeIndex';
import type { CreditPackage } from '../../types/credit.types';

type ExtendedCreditPackage = CreditPackage & { priceEur: number | null };

import type { VoucherApplicationState } from '../../types/voucher.types';
import { applyVoucher, calculateFinalPrice, findVoucherByCodeFromList, validateVoucherCode } from '../../utils/voucher.utils';
import type { BankTransferPaymentState } from './bankTransferPayment.view';

type PaymentMethodType = 'bankTransfer' | 'gopay' | 'gpwebpay';

const TopUpCreditView = () => {
    const navigate = useNavigate();
    const api = useApi();
    const { identity } = useSession();
    const toastNotifications = useToastNotifications();

    const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
    const [editBillingInfo, setEditBillingInfo] = useState(false);
    const [showTac, setShowTac] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [useVoucher, setUseVoucher] = useState(false);
    const [voucherCode, setVoucherCode] = useState('');
    const [voucherError, setVoucherError] = useState<string | undefined>(undefined);
    const [appliedVoucher, setAppliedVoucher] = useState<VoucherApplicationState | undefined>(undefined);
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType | null>(null);

    const { data: packagesData, isLoading, error } = useQuery({
        queryKey: ['credit-packages'],
        queryFn: () => api.v1.credits.getPackages(),
    });

    const packages: ExtendedCreditPackage[] = useMemo(() => {
        if (!packagesData) return [];
        return packagesData.map(pkg => ({
            id: pkg.packageId,
            price: pkg.price,
            priceEur: pkg.priceEur ?? null,
            credits: pkg.credits,
            currency: pkg.currency as 'CZK',
            label: pkg.label,
            description: pkg.description,
            descriptionSk: pkg.descriptionSk ?? null,
            isPopular: false,
            saleBadge: pkg.saleBadge,
        }));
    }, [packagesData]);

    const selectedPackage = useMemo(
        () => packages.find(p => p.id === selectedPackageId) ?? null,
        [packages, selectedPackageId]
    );

    const billingResidency = identity.userFields['billingResidency'] as BillingResidency;
    const hasBillingInfo = !!billingResidency;

    const configQuery = useQuery({
        queryKey: ['config', 'payment'],
        queryFn: () => api.v1.config.getPaymentConfig(),
        enabled: hasBillingInfo,
    });

    const vouchersQuery = useQuery({
        queryKey: ['vouchers', 'active'],
        queryFn: () => api.v1.vouchers.getActive(),
    });

    const findVoucherByCode = useMemo(
        () => findVoucherByCodeFromList((vouchersQuery.data ?? []) as any),
        [vouchersQuery.data]
    );

    const paymentConfig = configQuery.data && configQuery.data[billingResidency];
    const isEur = billingResidency === 'SK';
    const currencyFormatter = getCurrencyFormatter(paymentConfig?.currency ?? (isEur ? 'EUR' : 'CZK'), 'cs-CZ');

    const getPackagePrice = (pkg: ExtendedCreditPackage) =>
        isEur ? (pkg.priceEur ?? pkg.price) : pkg.price;

    const basePrice = selectedPackage ? getPackagePrice(selectedPackage) : 0;
    const finalPrice = selectedPackage
        ? calculateFinalPrice(basePrice, appliedVoucher)
        : 0;

    const buildPurchaseRequest = useCallback(() => {
        if (!selectedPackage) return null;
        return {
            residency: billingResidency,
            packages: [{
                packageId: selectedPackage.id,
                quantity: 1,
                credits: selectedPackage.credits,
                price: getPackagePrice(selectedPackage),
            }],
            totalAmount: finalPrice,
            totalCredits: selectedPackage.credits,
            voucherCode: appliedVoucher?.voucher.code,
        };
    }, [selectedPackage, billingResidency, finalPrice, appliedVoucher]);

    const gpWebpayMutation = useMutation({
        mutationFn: async () => {
            const req = buildPurchaseRequest();
            if (!req) throw new Error('No package selected');
            return api.v1.credits.purchaseWithGpWebpay(req);
        },
        onSuccess: (result) => {
            window.location.href = result.paymentUrl;
        },
        onError: () => {
            toastNotifications.showToast({
                type: ToastNotificationType.ERROR,
                message: 'Nepodařilo se vytvořit platbu. Zkuste to prosím znovu.',
            });
        },
    });

    const goPayMutation = useMutation({
        mutationFn: async () => {
            const req = buildPurchaseRequest();
            if (!req) throw new Error('No package selected');
            return api.v1.credits.purchaseWithGoPay(req);
        },
        onSuccess: (result) => {
            window.location.href = result.paymentUrl;
        },
        onError: () => {
            toastNotifications.showToast({
                type: ToastNotificationType.ERROR,
                message: 'Nepodařilo se vytvořit platbu. Zkuste to prosím znovu.',
            });
        },
    });

    const bankTransferMutation = useMutation({
        mutationFn: async () => {
            const req = buildPurchaseRequest();
            if (!req) throw new Error('No package selected');
            return api.v1.credits.purchaseWithBankTransfer(req);
        },
        onSuccess: (result) => {
            if (!selectedPackage) return;
            const navigationState: BankTransferPaymentState = {
                bankTransferOrder: result,
                checkoutSummary: {
                    selections: [{ package: { ...selectedPackage, price: basePrice }, quantity: 1 }],
                    totalPrice: basePrice,
                    totalCredits: selectedPackage.credits,
                    currency: isEur ? 'EUR' : 'CZK',
                },
                finalPrice,
                billingResidency,
                appliedVoucher,
            };
            clearPackageSelections();
            navigate(pathWithParams(RouteIndex.bankTransferPayment, {}), { state: navigationState });
        },
        onError: () => {
            toastNotifications.showToast({
                type: ToastNotificationType.ERROR,
                message: 'Nepodařilo se vytvořit objednávku. Zkuste to prosím znovu.',
            });
        },
    });

    const handleSelectPackage = useCallback((pkgId: string) => {
        setSelectedPackageId(pkgId);
        setAppliedVoucher(undefined);
        setVoucherCode('');
        setVoucherError(undefined);
        setUseVoucher(false);
        setPaymentMethod(null);
    }, []);

    const handleVoucherSubmit = useCallback(() => {
        if (!selectedPackage) return;
        setVoucherError(undefined);
        const result = validateVoucherCode(voucherCode, selectedPackage.price, findVoucherByCode);
        if (!result.isValid) {
            setVoucherError(result.error);
            return;
        }
        if (result.voucher) {
            setAppliedVoucher(applyVoucher(result.voucher, selectedPackage.price));
            toastNotifications.showToast({
                type: ToastNotificationType.SUCCESS,
                message: `Slevový kód "${result.voucher.code}" byl úspěšně použit!`,
            });
        }
    }, [voucherCode, selectedPackage, findVoucherByCode, toastNotifications]);

    const handlePay = useCallback(() => {
        if (!selectedPackage || !paymentMethod) return;
        if (paymentMethod === 'gpwebpay') gpWebpayMutation.mutate();
        else if (paymentMethod === 'gopay') goPayMutation.mutate();
        else if (paymentMethod === 'bankTransfer') bankTransferMutation.mutate();
    }, [selectedPackage, paymentMethod, gpWebpayMutation, goPayMutation, bankTransferMutation]);

    const isPaymentPending = gpWebpayMutation.isPending || goPayMutation.isPending || bankTransferMutation.isPending;
    const canPay = !!selectedPackage && !!paymentMethod && hasBillingInfo && !editBillingInfo;
    const backLink = getCreditTopUpReturnPath() ?? RouteIndex.wallet;

    return (
        <>
            <AppHeader pageTitle='Dobít Kredit' backLink={backLink} />

            <div className='p-4 pt-16 md:pt-20 md:pb-12'>
                <div className='max-w-2xl mx-auto'>

                    {isLoading && (
                        <div className='flex justify-center items-center py-12'>
                            <Spinner size='xl' />
                        </div>
                    )}

                    {error && (
                        <ErrorMessage>
                            Nepodařilo se načíst dostupné balíčky kreditů. Zkuste to prosím znovu.
                        </ErrorMessage>
                    )}

                    {!isLoading && !error && (
                        <div className='flex flex-col gap-6'>

                            {/* Package selection */}
                            <div>
                                <h1 className='text-xl font-bold text-gray-900 mb-4'>Vyberte balíček</h1>
                                <div className='flex flex-col gap-3'>
                                    {packages.map((pkg) => {
                                        const isSelected = selectedPackageId === pkg.id;
                                        return (
                                            <button
                                                key={pkg.id}
                                                type='button'
                                                onClick={() => handleSelectPackage(pkg.id)}
                                                className={clsx(
                                                    'relative text-left rounded-lg border-2 px-4 py-3 transition-all duration-150 cursor-pointer',
                                                    isSelected
                                                        ? 'border-primary-500 bg-primary-50'
                                                        : 'border-gray-200 bg-white hover:border-primary-300'
                                                )}
                                            >
                                                {pkg.saleBadge && (
                                                    <span className='absolute -top-2.5 left-3 inline-flex items-center gap-1 rounded bg-red-600 px-2 py-0.5 text-xs font-bold uppercase text-white'>
                                                        <TagIcon size={11} />
                                                        {pkg.saleBadge}
                                                    </span>
                                                )}
                                                <div className='flex items-center justify-between gap-3'>
                                                    <div>
                                                        <div className='font-semibold text-gray-900'>{pkg.label}</div>
                                                        {(() => {
                                                            const desc = billingResidency === BillingResidency.SK && pkg.descriptionSk
                                                                ? pkg.descriptionSk
                                                                : pkg.description;
                                                            return desc ? <div className='text-xs text-gray-400 mt-0.5'>{desc}</div> : null;
                                                        })()}
                                                        <div className='text-sm text-gray-500'>{pkg.credits} kreditů</div>
                                                    </div>
                                                    <div className='flex items-center gap-2 flex-shrink-0'>
                                                        <span className='text-lg font-bold text-primary-600'>
                                                            {currencyFormatter.format(getPackagePrice(pkg))}
                                                        </span>
                                                        {isSelected && (
                                                            <div className='rounded-full bg-primary-500 p-0.5 text-white'>
                                                                <CheckIcon size={16} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Billing info */}
                            <div className='border rounded-lg p-4 bg-white'>
                                <div className='flex items-center justify-between mb-1'>
                                    <h2 className='font-semibold text-gray-900'>Fakturační údaje</h2>
                                    {hasBillingInfo && !editBillingInfo && (
                                        <button
                                            type='button'
                                            className='hyperlink text-sm'
                                            onClick={() => setEditBillingInfo(true)}
                                        >
                                            Upravit
                                        </button>
                                    )}
                                </div>

                                {(!hasBillingInfo || editBillingInfo) && (
                                    <BillingForm
                                        submitLabel='Uložit fakturační údaje'
                                        showSuccessToast={false}
                                        onSave={() => setEditBillingInfo(false)}
                                    />
                                )}

                                {hasBillingInfo && !editBillingInfo && (
                                    <p className='text-sm text-gray-600'>
                                        {identity.userFields['billingName']},&nbsp;
                                        {identity.userFields['billingStreet']}{identity.userFields['billingStreetNumber'] ? ` ${identity.userFields['billingStreetNumber']}` : ''},&nbsp;
                                        {identity.userFields['billingCity']},&nbsp;
                                        {identity.userFields['billingPostcode']},&nbsp;
                                        {billingResidency}
                                    </p>
                                )}
                            </div>

                            {/* Payment method + order button */}
                            {hasBillingInfo && !editBillingInfo && (
                                <div className='border rounded-lg p-4 bg-white flex flex-col gap-4'>

                                    {/* Voucher */}
                                    <div>
                                        {!useVoucher ? (
                                            <button
                                                type='button'
                                                className='hyperlink text-sm'
                                                onClick={() => setUseVoucher(true)}
                                            >
                                                Využít slevový poukaz
                                            </button>
                                        ) : (
                                            <div>
                                                <Label htmlFor='voucherCode' className='mb-1 block text-sm'>
                                                    Slevový poukaz
                                                </Label>
                                                <div className='flex gap-2 max-w-xs'>
                                                    <TextInput
                                                        id='voucherCode'
                                                        type='text'
                                                        icon={Percent}
                                                        sizing='sm'
                                                        color={voucherError ? 'failure' : appliedVoucher ? 'success' : 'default'}
                                                        className='flex-1'
                                                        value={voucherCode}
                                                        onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                                                        disabled={!!appliedVoucher}
                                                        placeholder='Slevový kód'
                                                    />
                                                    {!appliedVoucher ? (
                                                        <Button size='sm' color='primary' onClick={handleVoucherSubmit} disabled={!voucherCode.trim()}>
                                                            Vložit
                                                        </Button>
                                                    ) : (
                                                        <Button size='sm' color='light' onClick={() => { setAppliedVoucher(undefined); setVoucherCode(''); setVoucherError(undefined); }}>
                                                            Zrušit
                                                        </Button>
                                                    )}
                                                </div>
                                                {voucherError && <p className='mt-1 text-xs text-red-600'>{voucherError}</p>}
                                                {appliedVoucher && (
                                                    <p className='mt-1 text-xs text-green-600 flex items-center gap-1'>
                                                        <Check size={14} />
                                                        {appliedVoucher.voucher.description || 'Sleva byla použita'}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Payment methods */}
                                    <div>
                                        <h2 className='font-semibold text-gray-900 mb-3'>Způsob platby</h2>
                                        <div className='flex flex-row flex-wrap gap-2'>
                                            {paymentConfig?.gpWebpay?.enabled && (
                                                <PaymentMethodButton
                                                    icon='card'
                                                    label='Kartou online (GP WebPay)'
                                                    isActive={paymentMethod === 'gpwebpay'}
                                                    onClick={() => setPaymentMethod('gpwebpay')}
                                                />
                                            )}
                                            {paymentConfig?.goPay?.enabled && (
                                                <PaymentMethodButton
                                                    icon='card'
                                                    label='Kartou online'
                                                    isActive={paymentMethod === 'gopay'}
                                                    onClick={() => setPaymentMethod('gopay')}
                                                />
                                            )}
                                            {paymentConfig?.bankTransfer && (
                                                <PaymentMethodButton
                                                    icon='bankTransfer'
                                                    label='Bankovním převodem'
                                                    isActive={paymentMethod === 'bankTransfer'}
                                                    onClick={() => setPaymentMethod('bankTransfer')}
                                                />
                                            )}
                                        </div>

                                        {!!paymentConfig && !paymentConfig.bankTransfer && !paymentConfig.goPay?.enabled && !paymentConfig.gpWebpay?.enabled && (
                                            <div className='flex items-center gap-2 p-3 border border-gray-300 rounded-lg mt-3'>
                                                <AlertTriangleIcon size={20} className='text-orange-600 flex-shrink-0' />
                                                <p className='text-sm font-medium text-gray-800'>
                                                    Pro Vaše místo bydliště nejsou dostupné žádné platební metody.
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Order button */}
                                    <Button
                                        color='primary'
                                        size='lg'
                                        className='w-full'
                                        onClick={handlePay}
                                        disabled={!canPay || isPaymentPending}
                                    >
                                        {isPaymentPending ? (
                                            <>
                                                <Spinner size='sm' className='mr-2' />
                                                Zpracovávám...
                                            </>
                                        ) : (
                                            <>
                                                {selectedPackage && paymentMethod
                                                    ? `Objednat s povinností platby - ${currencyFormatter.format(finalPrice)}`
                                                    : 'Objednat s povinností platby'
                                                }
                                            </>
                                        )}
                                    </Button>
                                    <p className='text-xs text-gray-500 text-center'>
                                        Potvrzením a úhradou objednávky souhlasíte s našimi{' '}
                                        <button type='button' className='hyperlink' onClick={() => setShowTac(true)}>obchodními podmínkami</button>
                                        {' '}a se{' '}
                                        <button type='button' className='hyperlink' onClick={() => setShowPrivacy(true)}>zásadami ochrany osobních údajů</button>.
                                    </p>
                                </div>
                            )}

                        </div>
                    )}
                </div>
            </div>

            <TermsAndConditionsModal show={showTac} onClose={() => setShowTac(false)} />
            <DataPrivacyModal show={showPrivacy} onClose={() => setShowPrivacy(false)} />
        </>
    );
};

export default TopUpCreditView;

const PaymentMethodButton: FC<{
    icon: 'card' | 'bankTransfer';
    label: string;
    isActive: boolean;
    onClick: () => void;
}> = ({ icon, label, isActive, onClick }) => (
    <button
        type='button'
        onClick={onClick}
        className={clsx(
            'flex items-center gap-2 py-2 px-4 rounded-md cursor-pointer transition-colors duration-150 border text-sm font-medium',
            isActive
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-transparent text-primary-500 border-gray-300 hover:border-primary-400'
        )}
    >
        {icon === 'card' && <CreditCardIcon size={20} />}
        {icon === 'bankTransfer' && <LandmarkIcon size={20} />}
        {label}
    </button>
);
